import React, { useEffect, useState, useMemo, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, Box, Loader2, X, ArrowRight } from 'lucide-react';
import { useSubcategories } from '../hooks/useSubcategories';
import CategoryIcon from '../components/CategoryIcon';
import { usePageTitle } from '../hooks/usePageTitle';

const Viewer3D = lazy(() => import('../components/Viewer3D'));
const ErrorBoundary = lazy(() => import('../components/ErrorBoundary'));
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url = '') => (url.startsWith('http') ? url : `${BACKEND}${url}`);

const Card = ({ product, onOpen, getSubcategoryName }) => {
  const modelUrl = resolveUrl(product.model_3d_url);
  return (
    <div
      data-testid={`product-3d-card-${product.id}`}
      className="group border border-border hover:border-primary transition-all duration-500 bg-white overflow-hidden"
    >
      <div className="relative aspect-[4/3] bg-neutral-50 overflow-hidden">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-300">
            <Box className="w-16 h-16" strokeWidth={1} />
          </div>
        )}
        <button
          onClick={() => onOpen(product, modelUrl)}
          data-testid={`open-3d-${product.id}`}
          className="absolute bottom-4 right-4 flex items-center gap-2 bg-primary text-white px-4 py-2.5 uppercase tracking-widest text-[10px] transition-all duration-500 hover:bg-black shadow-lg md:opacity-0 md:group-hover:opacity-100"
        >
          <Box className="w-3.5 h-3.5" strokeWidth={1.5} />
          Ver em 3D
        </button>
      </div>
      <div className="p-6">
        <p className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          {product.category} · {getSubcategoryName(product.type)}
        </p>
        <h3 className="font-heading text-xl font-light mb-2 tracking-tight">{product.name}</h3>
        {product.dimensions_display && (
          <p className="text-xs text-muted-foreground mb-1">📐 {product.dimensions_display}</p>
        )}
        {product.materials_display && (
          <p className="text-xs text-muted-foreground mb-3">✦ {product.materials_display}</p>
        )}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <div>
            {product.promotional_price ? (
              <div className="flex items-baseline gap-2">
                <span className="text-xs text-muted-foreground line-through">
                  R$ {(product.original_price || product.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
                <span className="text-lg font-medium text-red-600">
                  R$ {product.promotional_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            ) : (
              <p className="text-lg font-medium">
                R$ {product.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            )}
          </div>
          <Link
            to={`/produto/${product.id}`}
            data-testid={`goto-product-${product.id}`}
            className="text-xs uppercase tracking-widest text-primary hover:text-primary/70 flex items-center gap-1"
          >
            Detalhes <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>
  );
};

const Catalog3D = () => {
  usePageTitle('Catálogo 3D');
  const { grouped, getName } = useSubcategories();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sub, setSub] = useState('all');
  const [active, setActive] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/products`, { signal: controller.signal });
        // Somente os que têm modelo 3D
        setProducts((res.data || []).filter((p) => p.model_3d_url));
      } catch (e) {
        if (!axios.isCancel(e)) console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (category !== 'all' && p.category !== category) return false;
      if (sub !== 'all' && p.type !== sub) return false;
      if (q && !`${p.name} ${p.description || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [products, search, category, sub]);

  const subOptions = category === 'all' ? [] : grouped[category] || [];

  return (
    <div data-testid="catalog-3d-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mb-12">
          <p className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Box className="w-3.5 h-3.5" strokeWidth={1.5} /> Experiência Imersiva
          </p>
          <h1 className="font-heading text-4xl md:text-6xl font-light tracking-tight mb-4">
            Catálogo 3D
          </h1>
          <p className="text-base font-light text-muted-foreground leading-relaxed max-w-2xl">
            Explore nossos móveis em 360°. Gire, aproxime e visualize cada detalhe antes de decidir.
            Toque em "Ver em 3D" para abrir o visualizador interativo.
          </p>
        </div>

        {/* Filtros */}
        <div className="mb-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 border border-border px-4 py-3 focus-within:border-primary transition-colors">
            <Search className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <input
              type="text"
              data-testid="search-3d"
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent outline-none text-sm"
            />
          </div>
          <select
            data-testid="filter-category-3d"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setSub('all');
            }}
            className="border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-primary"
          >
            <option value="all">Todas as categorias</option>
            <option value="residencial">Residencial</option>
            <option value="corporativo">Corporativo</option>
          </select>
          <select
            data-testid="filter-subcategory-3d"
            value={sub}
            onChange={(e) => setSub(e.target.value)}
            disabled={category === 'all'}
            className="border border-border bg-transparent px-4 py-3 text-sm outline-none focus:border-primary disabled:opacity-50"
          >
            <option value="all">Todas as subcategorias</option>
            {subOptions.map((s) => (
              <option key={s.id} value={s.slug}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-24 text-muted-foreground flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> Carregando modelos...
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-3d" className="text-center py-24 border border-dashed border-border">
            <Box className="w-14 h-14 mx-auto mb-4 text-neutral-300" strokeWidth={1} />
            <p className="text-muted-foreground mb-2">Nenhum produto com modelo 3D encontrado.</p>
            <p className="text-xs text-muted-foreground">
              O administrador pode anexar arquivos GLB, GLTF, OBJ ou FBX ao editar um produto.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {filtered.map((p) => (
              <Card key={p.id} product={p} onOpen={(prod, url) => setActive({ product: prod, url })} getSubcategoryName={getName} />
            ))}
          </div>
        )}
      </div>

      {/* Modal do visualizador 3D */}
      {active && (
        <div
          data-testid="viewer-3d-modal"
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-2 md:p-6"
          onClick={(e) => e.target === e.currentTarget && setActive(null)}
        >
          <div className="relative w-full h-full max-w-6xl max-h-[92vh] bg-neutral-900 shadow-2xl grid grid-rows-[1fr_auto] md:grid-rows-1 md:grid-cols-[1fr_320px]">
            <div className="relative">
              <Suspense
                fallback={
                  <div className="w-full h-full flex items-center justify-center text-white">
                    <Loader2 className="w-8 h-8 animate-spin" strokeWidth={1.5} />
                  </div>
                }
              >
                <ErrorBoundary>
                  <Viewer3D
                    url={active.url}
                    format={active.product.model_3d_format}
                    productId={active.product.id}
                    source="catalog_3d"
                    onClose={() => setActive(null)}
                  />
                </ErrorBoundary>
              </Suspense>
            </div>

            <div className="bg-white p-6 md:p-8 overflow-y-auto">
              <p className="font-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                {active.product.category} · {getName(active.product.type)}
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-light tracking-tight mb-4">
                {active.product.name}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                {active.product.description}
              </p>
              {active.product.dimensions_display && (
                <div className="mb-4">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Medidas</p>
                  <p className="text-sm">{active.product.dimensions_display}</p>
                </div>
              )}
              {active.product.materials_display && (
                <div className="mb-6">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Materiais</p>
                  <p className="text-sm">{active.product.materials_display}</p>
                </div>
              )}
              <div className="border-t border-border pt-4 mb-6">
                {active.product.promotional_price ? (
                  <div className="flex items-baseline gap-3">
                    <span className="text-xs text-muted-foreground line-through">
                      R$ {(active.product.original_price || active.product.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                    <span className="text-2xl font-medium text-red-600">
                      R$ {active.product.promotional_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ) : (
                  <p className="text-2xl font-medium">
                    R$ {active.product.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>
              <Link
                to={`/produto/${active.product.id}`}
                className="block w-full text-center bg-primary text-white py-4 uppercase tracking-widest text-xs hover:bg-black transition-all"
              >
                Ver detalhes e comprar
              </Link>
              <button
                onClick={() => setActive(null)}
                className="w-full text-center text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mt-4"
              >
                Fechar visualizador
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalog3D;
